import type { Position } from 'geojson';

/**
 * Modèle géologique d'un tir en blocs, construit à partir de ce qui a été relevé dans ses trous :
 * lithologies (en pourcentage de la longueur du trou) ou drill logs (en mètres).
 *
 * Chaque trou occupe sa cellule de la vue de dessus (Voronoï découpé au contour du tir), empilée
 * en tranches horizontales : une tranche prend la catégorie majoritaire relevée par le trou à
 * cette altitude. Un trou sans relevé laisse sa colonne vide, comme sa cellule reste sans couleur
 * en vue de dessus.
 */

export interface Vec2 { x: number; y: number }
export interface Vec3 extends Vec2 { z: number }

/** Tranche d'un trou : une catégorie (lithologie ou drill log) entre deux profondeurs. */
export interface GeologyInterval {
  id: number;
  from: number;
  to: number;
}

/**
 * Lithologies : profondeurs en pourcentage (0–100) de la longueur du trou.
 * Drill logs : profondeurs en mètres depuis la tête.
 */
export type GeologyKind = 'lithology' | 'drillLog';

/** Ce que la vue 3D d'un tir doit construire, posé par la légende de l'onglet Trous. */
export interface GeologyModel {
  kind: GeologyKind;
  /** Tranches de chaque trou (publicGuid). Une nouvelle Map donne un nouveau modèle à construire. */
  holes: ReadonlyMap<string, readonly GeologyInterval[]>;
  /** Cellule de chaque trou en vue de dessus, dans la projection du site : un anneau par morceau. */
  cells: ReadonlyMap<string, { rings: readonly Position[][] }>;
  /** Couleur de chaque catégorie. */
  colors: ReadonlyMap<number, string>;
  /** Catégories masquées depuis la légende : retirées de l'affichage sans reconstruire le modèle. */
  hidden: ReadonlySet<number>;
}

/** Un trou dans le repère de la scène : sa trajectoire de la tête au pied et ses tranches. */
export interface HoleTrack {
  path: Vec3[];
  /**
   * Longueur déclarée du trou (m), à laquelle se rapportent les profondeurs relevées. Elle peut
   * différer de la longueur de la trajectoire déviée : les profondeurs y sont ramenées au prorata,
   * comme les charges.
   */
  length: number;
  intervals: readonly GeologyInterval[];
}

/**
 * Une cellule de la vue de dessus et les trous qui l'occupent : un seul en général, plusieurs
 * quand des trous partagent une tête (la vue de dessus leur donne alors la même cellule).
 */
export interface HoleColumn {
  holes: readonly HoleTrack[];
  rings: readonly (readonly Vec2[])[];
}

/** Un bloc du modèle : une cellule entre deux altitudes, d'une seule catégorie. */
export interface GeologyBlock {
  id: number;
  bottom: number;
  top: number;
  rings: readonly (readonly Vec2[])[];
}

/** Hauteur d'une tranche (m). */
export const LAYER_HEIGHT = 1;

/** Couleur d'une catégorie sans couleur dans le référentiel du site, en 3D comme dans la légende. */
export const GEOLOGY_FALLBACK_COLOR = '#9a9a9a';

/** Longueur ou hauteur négligeable (m) : en dessous, c'est un reste d'arrondi, pas un relevé. */
const EPSILON = 1e-6;

/**
 * Blocs du modèle : pour chaque cellule, une tranche par altitude où ses trous ont relevé quelque
 * chose. Chaque tranche relevée est découpée aux segments de la trajectoire puis aux limites des
 * tranches d'altitude : chaque tranche d'altitude reçoit la longueur exacte de chaque catégorie qui
 * la traverse, si courte soit-elle, et prend la plus longue.
 */
export function buildBlocks(columns: Iterable<HoleColumn>, kind: GeologyKind, layer = LAYER_HEIGHT): GeologyBlock[] {
  const blocks: GeologyBlock[] = [];

  for (const column of columns) {
    if (!column.rings.length) continue;
    const lengthsByBand = new Map<number, Map<number, number>>();
    let highest = -Infinity;
    let lowest = Infinity;

    for (const hole of column.holes) {
      const path = hole.path;
      const lengths = cumulativeLengths(path);
      const pathLength = lengths[lengths.length - 1] ?? 0;
      // Trou de longueur inconnue : la légende ne le compte pas, le modèle ne le dessine pas.
      if (!(pathLength > 0) || !(hole.length > 0)) continue;

      // Les blocs s'arrêtent aux têtes et aux pieds des trous : le dessus du modèle suit les têtes.
      for (const point of path) {
        highest = Math.max(highest, point.z);
        lowest = Math.min(lowest, point.z);
      }

      const toMetres = metresPerUnit(kind, hole.length);
      const toPath = pathLength / hole.length;
      for (const interval of hole.intervals) {
        // Coupée à la longueur déclarée (comme les parts de la légende), puis placée sur la trajectoire.
        const from = clamp(Math.min(interval.from, interval.to) * toMetres, hole.length) * toPath;
        const to = clamp(Math.max(interval.from, interval.to) * toMetres, hole.length) * toPath;
        // Tranche vide, ou entièrement hors du trou une fois ramenée à sa longueur : rien de relevé.
        if (!(to - from > EPSILON)) continue;

        for (let i = 1; i < path.length; i++) {
          const start = Math.max(from, lengths[i - 1]);
          const end = Math.min(to, lengths[i]);
          if (!(end - start > EPSILON)) continue;
          const span = lengths[i] - lengths[i - 1];
          const zAt = (distanceFromCollar: number): number =>
            path[i - 1].z + (path[i].z - path[i - 1].z) * ((distanceFromCollar - lengths[i - 1]) / span);
          addToBands(lengthsByBand, interval.id, zAt(start), zAt(end), end - start, layer);
        }
      }
    }

    for (const [band, cell] of lengthsByBand) {
      const bottom = Math.max(band * layer, lowest);
      const top = Math.min((band + 1) * layer, highest);
      if (top - bottom > EPSILON) blocks.push({ id: majority(cell), bottom, top, rings: column.rings });
    }
  }
  return blocks;
}

/** Hauteurs `[bas, haut]` (m) d'un morceau de mur à dessiner. */
export type ZRange = readonly [bottom: number, top: number];

/**
 * Faces à dessiner d'un bloc : dessus, dessous, et pour chaque anneau, pour chaque arête
 * (i → i + 1), les morceaux de mur à dessiner — aucun si la cellule voisine couvre tout le mur.
 */
export interface BlockFaces {
  top: boolean;
  bottom: boolean;
  walls: ZRange[][][];
}

/** Arête de cellule identifiée par ses deux extrémités au millimètre, dans un sens ou dans l'autre : deux cellules voisines la partagent. */
export function edgeKey(a: Vec2, b: Vec2): string {
  const pa = `${Math.round(a.x * 1000)},${Math.round(a.y * 1000)}`;
  const pb = `${Math.round(b.x * 1000)},${Math.round(b.y * 1000)}`;
  return pa < pb ? `${pa}|${pb}` : `${pb}|${pa}`;
}

/**
 * Faces extérieures des blocs affichés : un bloc n'a pas de dessus (ou de dessous) quand un bloc de
 * sa colonne le touche au-dessus (ou au-dessous), et ses murs perdent la hauteur couverte par le
 * bloc de la cellule voisine à la même altitude. La transparence ne s'empile ainsi pas d'un bloc à
 * l'autre, et la coupe du curseur comme les couches masquées laissent des faces fermées.
 * `edgeOwners` donne, pour chaque arête (edgeKey), les colonnes (anneaux d'une cellule) qui la bordent.
 */
export function exteriorFaces(
  blocks: readonly GeologyBlock[],
  edgeOwners: ReadonlyMap<string, ReadonlySet<GeologyBlock['rings']>>,
  layer = LAYER_HEIGHT,
): BlockFaces[] {
  // Un bloc au plus par colonne et par tranche d'altitude.
  const bandOf = (block: GeologyBlock): number => Math.floor((block.bottom + block.top) / 2 / layer);
  const columns = new Map<GeologyBlock['rings'], Map<number, GeologyBlock>>();
  for (const block of blocks) {
    const column = columns.get(block.rings) ?? new Map<number, GeologyBlock>();
    column.set(bandOf(block), block);
    columns.set(block.rings, column);
  }

  return blocks.map(block => {
    const band = bandOf(block);
    const column = columns.get(block.rings);
    const above = column?.get(band + 1);
    const below = column?.get(band - 1);
    const walls = block.rings.map(ring => ring.map((from, index) => {
      let spans: ZRange[] = [[block.bottom, block.top]];
      for (const owner of edgeOwners.get(edgeKey(from, ring[(index + 1) % ring.length])) ?? []) {
        if (owner === block.rings) continue;
        const neighbour = columns.get(owner)?.get(band);
        if (neighbour) spans = withoutRange(spans, neighbour.bottom, neighbour.top);
      }
      return spans;
    }));
    return {
      top: !(above && Math.abs(above.bottom - block.top) < EPSILON),
      bottom: !(below && Math.abs(below.top - block.bottom) < EPSILON),
      walls,
    };
  });
}

/** Morceaux de `spans` hors de `[low, high]`, sans les restes négligeables. */
function withoutRange(spans: readonly ZRange[], low: number, high: number): ZRange[] {
  const kept: ZRange[] = [];
  for (const [bottom, top] of spans) {
    if (high <= bottom + EPSILON || low >= top - EPSILON) {
      kept.push([bottom, top]);
      continue;
    }
    if (low - bottom > EPSILON) kept.push([bottom, low]);
    if (top - high > EPSILON) kept.push([high, top]);
  }
  return kept;
}

/**
 * Répartit `length` mètres d'une catégorie, relevés en ligne droite de l'altitude `za` à `zb`,
 * entre les tranches d'altitude traversées, au prorata de la hauteur parcourue dans chacune.
 */
function addToBands(lengthsByBand: Map<number, Map<number, number>>, id: number, za: number, zb: number, length: number, layer: number): void {
  const add = (band: number, part: number): void => {
    const cell = lengthsByBand.get(band) ?? new Map<number, number>();
    cell.set(id, (cell.get(id) ?? 0) + part);
    lengthsByBand.set(band, cell);
  };

  const low = Math.min(za, zb);
  const high = Math.max(za, zb);
  // Morceau horizontal : tout dans la tranche de son altitude.
  if (high - low < EPSILON) {
    add(Math.floor((low + high) / 2 / layer), length);
    return;
  }
  for (let band = Math.floor(low / layer); band * layer < high; band++) {
    const overlap = Math.min(high, (band + 1) * layer) - Math.max(low, band * layer);
    // Un morceau qui finit pile sur une limite ne déborde pas d'un reste d'arrondi sur la tranche voisine.
    if (overlap > EPSILON) add(band, (length * overlap) / (high - low));
  }
}

/**
 * Facteur qui convertit les profondeurs des tranches d'un trou de `holeLength` mètres en mètres
 * depuis la tête. Lithologies : pourcentages 0–100, contrat de l'API (GraphReading.API,
 * HoleGeologyType) — une tranche à 0–1 % reste 1 % du trou, pas le trou entier.
 */
export function metresPerUnit(kind: GeologyKind, holeLength: number): number {
  return kind === 'lithology' ? holeLength / 100 : 1;
}

/** Couleur saisie en config : `#rrggbb` ou `#rgb`, avec ou sans `#` ; `fallback` si illisible. */
export function normalizeHexColor(color: string | null | undefined, fallback: string): string {
  const value = color?.trim() ?? '';
  if (/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) return value.startsWith('#') ? value : `#${value}`;
  return fallback;
}

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(0, Number.isFinite(value) ? value : 0));
}

/** Catégorie couvrant la plus grande longueur de la tranche. */
function majority(cell: Map<number, number>): number {
  let best = 0;
  let bestLength = -1;
  for (const [id, length] of cell) {
    if (length > bestLength) {
      best = id;
      bestLength = length;
    }
  }
  return best;
}

/** Longueur cumulée le long de la trajectoire, du premier point à chacun des suivants. */
function cumulativeLengths(path: readonly Vec3[]): number[] {
  const lengths = path.length ? [0] : [];
  for (let i = 1; i < path.length; i++) lengths.push(lengths[i - 1] + distance(path[i - 1], path[i]));
  return lengths;
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
}
